import { useState, useRef, useEffect } from "react";
import { useAuth } from "./AuthContext";
import AuthModal from "./AuthModal";
import HistoryPanel from "./HistoryPanel";
import VisualizeModal from "./VisualizeModal";
import VisualizationsPanel from "./VisualizationsPanel";
import AgentPanel from "./AgentPanel";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

// --- Theme ---
const theme = {
  bg: "#0a0f1a",
  surface: "#111827",
  surfaceHover: "#1a2234",
  border: "#1e2a3a",
  accent: "#00e5a0",
  accentDim: "rgba(0, 229, 160, 0.12)",
  accentGlow: "rgba(0, 229, 160, 0.3)",
  text: "#e2e8f0",
  textMuted: "#64748b",
  textDim: "#475569",
  error: "#f87171",
  errorBg: "rgba(248, 113, 113, 0.08)",
  warning: "#fbbf24",
};

// --- Styles ---
const styles = {
  app: {
    minHeight: "100vh",
    background: `linear-gradient(170deg, ${theme.bg} 0%, #0d1525 40%, #0f1a2e 100%)`,
    color: theme.text,
    fontFamily: "'IBM Plex Mono', 'JetBrains Mono', 'Fira Code', monospace",
    position: "relative",
    overflow: "hidden",
  },
  gridOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      linear-gradient(${theme.border}22 1px, transparent 1px),
      linear-gradient(90deg, ${theme.border}22 1px, transparent 1px)
    `,
    backgroundSize: "60px 60px",
    pointerEvents: "none",
    zIndex: 0,
  },
  content: {
    position: "relative",
    zIndex: 1,
    maxWidth: 960,
    margin: "0 auto",
    padding: "0 24px",
  },
  header: {
    paddingTop: 80,
    paddingBottom: 48,
    textAlign: "center",
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
    marginBottom: 16,
  },
  logoIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: `linear-gradient(135deg, ${theme.accent}, #00b880)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    fontWeight: 800,
    color: theme.bg,
    letterSpacing: -1,
    fontFamily: "'IBM Plex Mono', monospace",
  },
  title: {
    fontSize: 42,
    fontWeight: 700,
    letterSpacing: -2,
    color: theme.text,
    margin: 0,
    fontFamily: "'IBM Plex Mono', monospace",
  },
  titleAccent: {
    color: theme.accent,
  },
  subtitle: {
    fontSize: 15,
    color: theme.textMuted,
    marginTop: 10,
    letterSpacing: 0.5,
    fontWeight: 400,
  },
  searchContainer: {
    position: "relative",
    marginBottom: 40,
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    background: theme.surface,
    border: `1.5px solid ${theme.border}`,
    borderRadius: 14,
    padding: "6px 6px 6px 20px",
    transition: "all 0.25s ease",
    boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
  },
  searchBoxFocused: {
    borderColor: theme.accent,
    boxShadow: `0 4px 24px rgba(0,0,0,0.2), 0 0 0 3px ${theme.accentDim}`,
  },
  input: {
    flex: 1,
    background: "transparent",
    border: "none",
    outline: "none",
    color: theme.text,
    fontSize: 16,
    fontFamily: "'IBM Plex Mono', monospace",
    padding: "14px 0",
    letterSpacing: 0.2,
  },
  submitBtn: {
    background: `linear-gradient(135deg, ${theme.accent}, #00c98a)`,
    border: "none",
    borderRadius: 10,
    padding: "12px 28px",
    color: theme.bg,
    fontSize: 14,
    fontWeight: 700,
    fontFamily: "'IBM Plex Mono', monospace",
    cursor: "pointer",
    letterSpacing: 0.8,
    textTransform: "uppercase",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
  },
  submitBtnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  exampleQueries: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "center",
    marginTop: 16,
  },
  exampleChip: {
    background: theme.accentDim,
    border: `1px solid ${theme.accent}33`,
    borderRadius: 20,
    padding: "6px 14px",
    fontSize: 12,
    color: theme.accent,
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontFamily: "'IBM Plex Mono', monospace",
    letterSpacing: 0.3,
  },
  resultSection: {
    marginBottom: 60,
  },
  resultCard: {
    background: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
  },
  resultHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px",
    borderBottom: `1px solid ${theme.border}`,
    background: `linear-gradient(135deg, ${theme.accentDim}, transparent)`,
  },
  resultQuestion: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.text,
    margin: 0,
  },
  rowCount: {
    fontSize: 12,
    color: theme.textMuted,
    background: theme.bg,
    padding: "4px 10px",
    borderRadius: 6,
  },
  sqlToggle: {
    padding: "10px 20px",
    borderBottom: `1px solid ${theme.border}`,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  sqlToggleBtn: {
    background: "none",
    border: "none",
    color: theme.textMuted,
    fontSize: 12,
    cursor: "pointer",
    fontFamily: "'IBM Plex Mono', monospace",
    padding: 0,
    display: "flex",
    alignItems: "center",
    gap: 6,
    transition: "color 0.2s",
  },
  sqlBlock: {
    padding: "12px 20px",
    background: theme.bg,
    borderBottom: `1px solid ${theme.border}`,
    fontSize: 13,
    color: theme.accent,
    lineHeight: 1.6,
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontFamily: "'IBM Plex Mono', monospace",
  },
  tableWrapper: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
  },
  th: {
    textAlign: "left",
    padding: "12px 16px",
    color: theme.accent,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1.2,
    borderBottom: `1px solid ${theme.border}`,
    background: theme.bg,
    position: "sticky",
    top: 0,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "11px 16px",
    borderBottom: `1px solid ${theme.border}44`,
    color: theme.text,
    whiteSpace: "nowrap",
  },
  trHover: {
    background: theme.surfaceHover,
  },
  emptyState: {
    padding: "40px 20px",
    textAlign: "center",
    color: theme.textMuted,
    fontSize: 14,
  },
  errorCard: {
    background: theme.errorBg,
    border: `1px solid ${theme.error}33`,
    borderRadius: 14,
    padding: 24,
    marginBottom: 40,
  },
  errorTitle: {
    color: theme.error,
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  errorMsg: {
    color: theme.textMuted,
    fontSize: 13,
    lineHeight: 1.6,
    margin: 0,
  },
  errorSql: {
    marginTop: 12,
    padding: 12,
    background: theme.bg,
    borderRadius: 8,
    fontSize: 12,
    color: theme.textDim,
    overflowX: "auto",
    fontFamily: "'IBM Plex Mono', monospace",
  },
  loadingDots: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "48px 0",
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: theme.accent,
  },
  footer: {
    textAlign: "center",
    padding: "40px 0 32px",
    color: theme.textDim,
    fontSize: 12,
    letterSpacing: 0.5,
  },
  footerLink: {
    color: theme.textMuted,
    textDecoration: "none",
  },
};

// --- Helpers ---
function rowsToObjects(columns, rows) {
  return rows.map((row) => {
    const obj = {};
    columns.forEach((col, i) => { obj[col] = row[i]; });
    return obj;
  });
}

// --- Loading animation ---
function LoadingDots() {
  return (
    <div style={styles.loadingDots}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            ...styles.dot,
            animation: `pulse 1.2s ease-in-out ${i * 0.15}s infinite`,
          }}
        />
      ))}
      <style>{`
        @keyframes pulse {
          0%, 80%, 100% { transform: scale(0.4); opacity: 0.3; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// --- Results Table ---
function ResultsTable({ data: initialData, onRetryResult, queryId, isSelected, onToggleSelect }) {
  const { session } = useAuth();
  const [data, setData] = useState(initialData);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [showSql, setShowSql] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [flagLoading, setFlagLoading] = useState(false);
  const [flagError, setFlagError] = useState(null);

  useEffect(() => {
    setData(initialData);
  }, [initialData]);

  if (!data) return null;

  const handleFlagSubmit = async () => {
    if (!feedback.trim()) return;
    setFlagLoading(true);
    setFlagError(null);
    try {
      const res = await fetch(`${API_URL}/flag`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          user_prompt: data.question,
          generated_sql: data.sql,
          user_feedback: feedback.trim(),
        }),
      });
      const result = await res.json();
      if (!res.ok) {
        const detail = result.detail || {};
        setFlagError(detail.error || "Something went wrong. Try again.");
      } else {
        setData(result);
        setFlagOpen(false);
        setFeedback("");
        if (onRetryResult) onRetryResult(result);
      }
    } catch {
      setFlagError("Could not connect to the server.");
    } finally {
      setFlagLoading(false);
    }
  };

  return (
    <div style={styles.resultSection}>
      <div style={styles.resultCard}>
        <div style={styles.resultHeader}>
          <p style={styles.resultQuestion}>{data.question}</p>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {onToggleSelect && queryId && data.rows.length > 0 && (
              <button
                onClick={() =>
                  onToggleSelect({
                    id: queryId,
                    prompt: data.question,
                    sql: data.sql,
                    results: rowsToObjects(data.columns, data.rows),
                  })
                }
                style={{
                  background: isSelected ? theme.accentDim : "transparent",
                  border: `1px solid ${isSelected ? theme.accent : theme.border}`,
                  borderRadius: 6,
                  padding: "4px 10px",
                  color: isSelected ? theme.accent : theme.textMuted,
                  fontSize: 11,
                  fontFamily: "'IBM Plex Mono', monospace",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  letterSpacing: 0.3,
                }}
              >
                {isSelected ? "✓ Selected" : "+ Select"}
              </button>
            )}
            <span style={styles.rowCount}>
              {data.row_count} result{data.row_count !== 1 ? "s" : ""}
              {data.truncated ? " (truncated)" : ""}
            </span>
          </div>
        </div>

        <div style={{ ...styles.sqlToggle, justifyContent: "space-between" }}>
          <button
            style={styles.sqlToggleBtn}
            onClick={() => setShowSql(!showSql)}
            onMouseEnter={(e) => (e.target.style.color = theme.accent)}
            onMouseLeave={(e) => (e.target.style.color = theme.textMuted)}
          >
            <span style={{ fontSize: 10, transition: "transform 0.2s", transform: showSql ? "rotate(90deg)" : "rotate(0deg)", display: "inline-block" }}>▶</span>
            {showSql ? "Hide" : "Show"} SQL
          </button>
          {!flagOpen && (
            <button
              style={{ ...styles.sqlToggleBtn, color: theme.textDim }}
              onClick={() => setFlagOpen(true)}
              onMouseEnter={(e) => (e.target.style.color = theme.warning)}
              onMouseLeave={(e) => (e.target.style.color = theme.textDim)}
            >
              ⚑ Flag Result
            </button>
          )}
        </div>

        {showSql && <div style={styles.sqlBlock}>{data.sql}</div>}

        {flagOpen && (
          <div style={{ padding: "12px 20px", borderBottom: `1px solid ${theme.border}`, background: "rgba(251,191,36,0.05)" }}>
            <p style={{ fontSize: 12, color: theme.warning, marginBottom: 8, marginTop: 0 }}>
              What's wrong with this result?
            </p>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value.slice(0, 500))}
              placeholder="e.g. This should be sorted by receiving yards, not passing yards."
              style={{
                width: "100%",
                boxSizing: "border-box",
                background: theme.bg,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                color: theme.text,
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 13,
                padding: "10px 12px",
                resize: "vertical",
                minHeight: 72,
                outline: "none",
              }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
              <span style={{ fontSize: 11, color: theme.textDim }}>{feedback.length}/500</span>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => { setFlagOpen(false); setFeedback(""); setFlagError(null); }}
                  style={{ background: "none", border: `1px solid ${theme.border}`, borderRadius: 6, padding: "6px 14px", color: theme.textMuted, fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleFlagSubmit}
                  disabled={flagLoading || !feedback.trim()}
                  style={{ background: theme.accentDim, border: `1px solid ${theme.accent}33`, borderRadius: 6, padding: "6px 14px", color: theme.accent, fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", cursor: flagLoading || !feedback.trim() ? "not-allowed" : "pointer", opacity: flagLoading || !feedback.trim() ? 0.5 : 1 }}
                >
                  {flagLoading ? "Retrying..." : "Submit & Retry"}
                </button>
              </div>
            </div>
            {flagError && <p style={{ fontSize: 12, color: theme.error, marginTop: 8, marginBottom: 0 }}>{flagError}</p>}
          </div>
        )}

        <div style={styles.tableWrapper}>
          {data.rows.length > 0 ? (
            <table style={styles.table}>
              <thead>
                <tr>
                  {data.columns.map((col) => (
                    <th key={col} style={styles.th}>
                      {col.replace(/_/g, " ")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => (
                  <tr
                    key={i}
                    style={hoveredRow === i ? styles.trHover : {}}
                    onMouseEnter={() => setHoveredRow(i)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    {row.map((cell, j) => (
                      <td key={j} style={styles.td}>
                        {cell === null ? (
                          <span style={{ color: theme.textDim }}>—</span>
                        ) : (
                          String(cell)
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={styles.emptyState}>
              No results found. Try rephrasing your question.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Inline error banner (for non-NFL input) ---
function InlineErrorBanner({ error }) {
  if (!error) return null;
  return (
    <div style={{
      marginTop: 10,
      padding: "10px 16px",
      background: "rgba(251, 191, 36, 0.08)",
      border: `1px solid ${theme.warning}44`,
      borderRadius: 10,
      fontSize: 13,
      color: theme.warning,
    }}>
      ⚠ Please ask a question about NFL statistics.
    </div>
  );
}

// --- Error display ---
function ErrorDisplay({ error }) {
  if (!error) return null;
  const message = error.error || error.message || "Something went wrong.";
  const typeName = error.type;
  return (
    <div style={styles.errorCard}>
      <div style={styles.errorTitle}>
        <span>⚠</span> Query Failed
      </div>
      <p style={styles.errorMsg}>
        {message}
        {typeName && <span style={{ opacity: 0.6 }}> ({typeName})</span>}
      </p>
      {error.sql && <div style={styles.errorSql}>{error.sql}</div>}
    </div>
  );
}

// --- Example queries ---
const EXAMPLES = [
  "Who led the NFL in passing yards in 2023?",
  "What team had the most sacks in a single game?",
  "Shortest QB to throw for 4000+ yards?",
  "Most turnovers forced in a single game by one player?",
  "Which team scored the most points from 2020-2024?",
];

// --- Main App ---
export default function App() {
  const { user, session, signOut } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [activeTab, setActiveTab] = useState("query");
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [inlineError, setInlineError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);

  // Visualization state
  const [selectedQueries, setSelectedQueries] = useState([]);
  const [showVisualizeModal, setShowVisualizeModal] = useState(false);
  const [sessionVisualizations, setSessionVisualizations] = useState([]);

  const toggleQuerySelection = (queryData) => {
    setSelectedQueries((prev) => {
      const isSelected = prev.some((q) => q.id === queryData.id);
      if (isSelected) return prev.filter((q) => q.id !== queryData.id);
      if (prev.length >= 5) return prev;
      return [...prev, queryData];
    });
  };

  useEffect(() => {
    // Load Google Font
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600;700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    // Set body background
    document.body.style.margin = "0";
    document.body.style.background = theme.bg;
  }, []);

  const handleSubmit = async () => {
    if (!query.trim() || loading) return;

    setLoading(true);
    setError(null);
    setInlineError(null);
    setResult(null);

    try {
      const res = await fetch(`${API_URL}/query`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ question: query.trim() }),
      });

      let data;
      try {
        data = await res.json();
      } catch {
        setError({ error: `Server error (HTTP ${res.status}) — response was not JSON.` });
        setLoading(false);
        return;
      }

      if (!res.ok) {
        if (res.status === 429) {
          setError({ error: "Too many requests. Please wait a moment and try again." });
        } else {
          const detail = data.detail || { error: "Request failed" };
          if (detail.error === "Please ask a question about NFL statistics.") {
            setInlineError(detail);
          } else {
            setError(detail);
          }
        }
      } else {
        const resultWithId = { ...data, _vizId: `q-${Date.now()}` };
        setResult(resultWithId);
        setHistory((prev) => [resultWithId, ...prev].slice(0, 10));
      }
    } catch (err) {
      setError({ error: "Could not connect to the server. Is the backend running?" });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  const handleExample = (q) => {
    setQuery(q);
    inputRef.current?.focus();
  };

  return (
    <div style={styles.app}>
      <div style={styles.gridOverlay} />

      <div style={styles.content}>
        {/* Header */}
        <header style={{ ...styles.header, position: "relative" }}>
          <div style={styles.logoRow}>
            <div style={styles.logoIcon}>S</div>
            <h1 style={styles.title}>
              Stat<span style={styles.titleAccent}>It</span>
            </h1>
          </div>
          <p style={styles.subtitle}>
            Ask NFL stats questions in plain English. Powered by 25 years of data.
          </p>
          {/* Auth controls */}
          <div style={{ position: "absolute", top: 80, right: 0, display: "flex", alignItems: "center", gap: 10 }}>
            {user ? (
              <>
                <span style={{ fontSize: 12, color: theme.textMuted, fontFamily: "'IBM Plex Mono', monospace" }}>
                  {user.email}
                </span>
                <button
                  onClick={() => signOut()}
                  style={{ background: "none", border: `1px solid ${theme.border}`, borderRadius: 8, padding: "6px 12px", color: theme.textMuted, fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", cursor: "pointer" }}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <button
                onClick={() => setShowAuth(true)}
                style={{ background: theme.accentDim, border: `1px solid ${theme.accent}33`, borderRadius: 8, padding: "6px 16px", color: theme.accent, fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", cursor: "pointer" }}
              >
                Sign In / Sign Up
              </button>
            )}
          </div>
        </header>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 32 }}>
          {[
            { id: "query", label: "query" },
            { id: "agent", label: "agent" },
            { id: "history", label: "history" },
            { id: "visualizations", label: "visualizations", badge: sessionVisualizations.length || null },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                background: activeTab === tab.id ? theme.accentDim : "transparent",
                border: `1px solid ${activeTab === tab.id ? theme.accent : theme.border}`,
                borderRadius: 8,
                padding: "8px 20px",
                color: activeTab === tab.id ? theme.accent : theme.textMuted,
                fontSize: 13,
                fontFamily: "'IBM Plex Mono', monospace",
                cursor: "pointer",
                textTransform: "capitalize",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {tab.label}
              {tab.badge ? (
                <span
                  style={{
                    background: theme.accent,
                    color: theme.bg,
                    borderRadius: 10,
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "1px 6px",
                    lineHeight: 1.6,
                  }}
                >
                  {tab.badge}
                </span>
              ) : null}
            </button>
          ))}
        </div>

        {/* Agent panel — kept mounted (display toggle) so an in-flight run and its
            streamed output survive tab switches instead of being torn down. */}
        <div style={{ display: activeTab === "agent" ? "block" : "none" }}>
          <AgentPanel
            active={activeTab === "agent"}
            session={session}
            user={user}
            onSignInClick={() => setShowAuth(true)}
          />
        </div>

        {/* History panel */}
        {activeTab === "history" && (
          <HistoryPanel
            selectedQueryIds={selectedQueries.map((q) => q.id)}
            onToggleSelect={toggleQuerySelection}
          />
        )}

        {/* Visualizations panel */}
        {activeTab === "visualizations" && (
          <VisualizationsPanel
            sessionVisualizations={sessionVisualizations}
            onDeleteSession={(id) =>
              setSessionVisualizations((prev) => prev.filter((v) => v.visualization_id !== id))
            }
          />
        )}

        {/* Query tab content */}
        {activeTab === "query" && (
          <>
            <div style={styles.searchContainer}>
              <div style={{ ...styles.searchBox, ...(focused ? styles.searchBoxFocused : {}) }}>
                <input
                  ref={inputRef}
                  style={styles.input}
                  placeholder="e.g. Who has the most career interceptions?"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  onKeyDown={handleKeyDown}
                />
                <button
                  style={{
                    ...styles.submitBtn,
                    ...(loading || !query.trim() ? styles.submitBtnDisabled : {}),
                  }}
                  onClick={handleSubmit}
                  disabled={loading || !query.trim()}
                  onMouseEnter={(e) => {
                    if (!loading && query.trim()) e.target.style.transform = "translateY(-1px)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                  }}
                >
                  {loading ? "Querying..." : "Ask"}
                </button>
              </div>

              <InlineErrorBanner error={inlineError} />

              {/* Example chips */}
              {!result && !loading && (
                <div style={styles.exampleQueries}>
                  {EXAMPLES.map((q) => (
                    <div
                      key={q}
                      style={styles.exampleChip}
                      onClick={() => handleExample(q)}
                      onMouseEnter={(e) => {
                        e.target.style.background = theme.accentGlow;
                        e.target.style.borderColor = theme.accent;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = theme.accentDim;
                        e.target.style.borderColor = `${theme.accent}33`;
                      }}
                    >
                      {q}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {loading && <LoadingDots />}
            <ErrorDisplay error={error} />
            <ResultsTable
              data={result}
              queryId={result?._vizId}
              isSelected={selectedQueries.some((q) => q.id === result?._vizId)}
              onToggleSelect={toggleQuerySelection}
              onRetryResult={(corrected) => {
                const correctedWithId = { ...corrected, _vizId: result?._vizId };
                setResult(correctedWithId);
                setHistory((prev) => [correctedWithId, ...prev.slice(1)]);
              }}
            />

            {history.length > 1 && (
              <div style={{ marginTop: 20 }}>
                <p style={{ fontSize: 12, color: theme.textDim, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 16, fontWeight: 600 }}>
                  Previous Queries
                </p>
                {history.slice(1).map((item) => (
                  <ResultsTable
                    key={item._vizId}
                    data={item}
                    queryId={item._vizId}
                    isSelected={selectedQueries.some((q) => q.id === item._vizId)}
                    onToggleSelect={toggleQuerySelection}
                    onRetryResult={null}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <footer style={styles.footer}>
          StatIt — NFL data from{" "}
          <a
            href="https://github.com/nflverse"
            target="_blank"
            rel="noreferrer"
            style={styles.footerLink}
            onMouseEnter={(e) => (e.target.style.color = theme.accent)}
            onMouseLeave={(e) => (e.target.style.color = theme.textMuted)}
          >
            nflverse
          </a>{" "}
          (CC-BY-4.0) · 1999–2025
        </footer>
      </div>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}

      {/* Floating Visualize button — appears when 1-5 queries are selected */}
      {selectedQueries.length > 0 && !showVisualizeModal && (
        <button
          onClick={() => setShowVisualizeModal(true)}
          style={{
            position: "fixed",
            bottom: 32,
            right: 32,
            zIndex: 100,
            background: `linear-gradient(135deg, ${theme.accent}, #00c98a)`,
            border: "none",
            borderRadius: 12,
            padding: "14px 24px",
            color: theme.bg,
            fontSize: 14,
            fontWeight: 700,
            fontFamily: "'IBM Plex Mono', monospace",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 10,
            boxShadow: `0 8px 32px rgba(0, 229, 160, 0.35)`,
            transition: "all 0.2s ease",
            letterSpacing: 0.5,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="20" x2="18" y2="10" />
            <line x1="12" y1="20" x2="12" y2="4" />
            <line x1="6" y1="20" x2="6" y2="14" />
            <line x1="2" y1="20" x2="22" y2="20" />
          </svg>
          Visualize
          <span
            style={{
              background: theme.bg,
              color: theme.accent,
              borderRadius: 10,
              fontSize: 11,
              fontWeight: 700,
              padding: "1px 7px",
              lineHeight: 1.6,
            }}
          >
            {selectedQueries.length}
          </span>
        </button>
      )}

      {/* Visualization request modal */}
      {showVisualizeModal && (
        <VisualizeModal
          selectedQueries={selectedQueries}
          session={session}
          onClose={() => setShowVisualizeModal(false)}
          onSuccess={(vizData) => {
            setSessionVisualizations((prev) => [
              { ...vizData, _queryResults: [...selectedQueries] },
              ...prev,
            ]);
            setShowVisualizeModal(false);
            setSelectedQueries([]);
            setActiveTab("visualizations");
          }}
        />
      )}
    </div>
  );
}
